import { FormikProps } from 'formik';
import { bubbleIcons, ISettingsFormik } from '../../model';
import { FC, useRef } from 'react';
import { Field, RadioButton, Switch } from 'shared/ui';
import Divider from 'components/Divider';
import { HexColorInput } from 'react-colorful';
import ColorSelect from 'shared/ui/ColorSelect/ColorSelect';
import { cn, getErrorMessage, truncateText } from 'shared/lib';
import SelectInput from 'components/shared/SelectInput';
import { ITavusPersonaFormik } from 'entities/tavus/persona';
import { showError } from 'shared/model';
import { useUploadFile } from 'entities/asset';
import { BiX } from 'react-icons/bi';
import UploadPlusArea from 'shared/ui/Upload/UploadPlusArea';
import { Loader } from 'shared/ui/Loader';

interface IProps {
  formik: FormikProps<ITavusPersonaFormik>;
}
export const InterfaceSettings: FC<IProps> = ({ formik }) => {
  const { isLoading, uploadFile } = useUploadFile();

  const handleUpload = async (files: FileList) => {
    try {
      const data = await uploadFile(files[0], 'Image');
      if (!data) return;
      if (data.originalFileUrl) {
        formik.setFieldValue('widget.bubble_icon_url', data.originalFileUrl);
        formik.setFieldValue('widget.bubble_icon_custom', true);
        formik.setFieldValue('widget.bubble_icon_variant', 'custom');
      }
    } catch (error) {
      const message = getErrorMessage(
        error,
        'There was a problem when we tried to upload file. Please try again later.'
      );
      showError(message);
    }
  };

  return (
    <>
      <div className="ag-flex ag-justify-between">
        <label
          className="ag-cursor-pointer ag-flex ag-items-center ag-gap-2"
          htmlFor="main-color"
        >
          <RadioButton
            id="main-color"
            name="mainColor"
            value="one-color"
            checked={!formik.values.widget.more_personalised_colors}
            onChange={() =>
              formik.setFieldValue('widget.more_personalised_colors', false)
            }
          />
          <div className="ag-text-neutral-700 ag-font-medium">Main color</div>
        </label>
        <div className="ag-flex ag-gap-1">
          <HexColorInput
            prefixed
            color={formik.values.widget.main_color}
            onChange={(color) =>
              formik.setFieldValue('widget.main_color', color)
            }
            className="ag-h-8 ag-max-w-24 ag-rounded-lg ag-border ag-border-neutral-200 ag-py-1.5 ag-px-3"
          />
          <ColorSelect
            pickerClassName="-ag-ml-48"
            className="ag-w-8 ag-h-8 ag-rounded-lg ag-border ag-border-neutral-200"
            color={formik.values.widget.main_color}
            onChange={(color) => {
              formik.setFieldValue('widget.main_color', color);
            }}
          />
        </div>
      </div>
      <div>
        <label
          className="ag-flex ag-cursor-pointer"
          htmlFor="personalised-color"
        >
          <div className="ag-flex ag-gap-2">
            <RadioButton
              id="personalised-color"
              name="mainColor"
              value="personalised"
              checked={formik.values.widget.more_personalised_colors}
              onChange={() =>
                formik.setFieldValue('widget.more_personalised_colors', true)
              }
            />
            <div className="ag-text-neutral-700 ag-font-medium">
              More personalised colours
            </div>
          </div>
        </label>
        {formik.values.widget.more_personalised_colors && (
          <div className="ag-pl-8 ag-pt-4 ag-space-y-4">
            <div className="ag-text-neutral-600 ag-flex ag-justify-between ag-items-center">
              <div>Welcome Button</div>
              <div className="ag-flex ag-gap-1">
                <HexColorInput
                  color={formik.values.widget.welcome_button_color}
                  onChange={(color) =>
                    formik.setFieldValue('widget.welcome_button_color', color)
                  }
                  className="ag-h-8 ag-max-w-24 ag-rounded-lg ag-border ag-border-neutral-200 ag-py-1.5 ag-px-3"
                />
                <ColorSelect
                  pickerClassName="-ag-ml-48"
                  className="ag-w-8 ag-h-8 ag-rounded-lg ag-border ag-border-neutral-200"
                  color={formik.values.widget.welcome_button_color}
                  onChange={(color) => {
                    formik.setFieldValue('widget.welcome_button_color', color);
                  }}
                />
              </div>
            </div>
            {!formik.values.widget.bubble_icon_custom && (
              <>
                <div className="ag-text-neutral-600 ag-flex ag-justify-between ag-items-center">
                  <div>Bubble background</div>
                  <div className="ag-flex ag-gap-1">
                    <HexColorInput
                      prefixed
                      className="ag-h-8 ag-max-w-24 ag-rounded-lg ag-border ag-border-neutral-200 ag-py-1.5 ag-px-3"
                      color={formik.values.widget.bubble_background_color}
                      onChange={(color) => {
                        formik.setFieldValue(
                          'widget.bubble_background_color',
                          color
                        );
                      }}
                    />
                    <ColorSelect
                      pickerClassName="-ag-ml-48"
                      className="ag-w-8 ag-h-8 ag-rounded-lg ag-border ag-border-neutral-200"
                      color={formik.values.widget.bubble_background_color}
                      onChange={(color) => {
                        formik.setFieldValue(
                          'widget.bubble_background_color',
                          color
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="ag-text-neutral-600 ag-flex ag-justify-between ag-items-center">
                  <div>Bubble icon</div>
                  <div className="ag-flex ag-gap-1">
                    <HexColorInput
                      prefixed
                      className="ag-h-8 ag-max-w-24 ag-rounded-lg ag-border ag-border-neutral-200 ag-py-1.5 ag-px-3"
                      color={formik.values.widget.bubble_icon_color}
                      onChange={(color) => {
                        formik.setFieldValue('widget.bubble_icon_color', color);
                      }}
                    />
                    <ColorSelect
                      pickerClassName="-ag-ml-48"
                      className="ag-w-8 ag-h-8 ag-rounded-lg ag-border ag-border-neutral-200"
                      color={formik.values.widget.bubble_icon_color}
                      onChange={(color) => {
                        formik.setFieldValue('widget.bubble_icon_color', color);
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <Divider />
      {/* <div className="ag-flex ag-items-center ag-justify-between">
        <span className="ag-text-neutral-700 ag-font-medium">Dark Mode</span>
        <Switch
          onChange={(value) => formik.setFieldValue('widget.dark_mode', value)}
          checked={formik.values.widget.dark_mode}
        />
      </div>
      <Divider /> */}
      <div className="ag-flex ag-items-center ag-justify-between">
        <span className="ag-text-neutral-700 ag-font-medium">Font</span>
        <SelectInput
          name="select-language"
          options={[
            'Roboto',
            'Open Sans',
            'Lato',
            'Montserrat',
            'Poppins',
            'Raleway',
            'Oswald',
            'Merriweather',
            'Playfair Display',
            'Inter'
          ]}
          value={truncateText(formik.values.widget.font, 7)}
          onChange={(value) => formik.setFieldValue('widget.font', value)}
          optionsWrapperClassName="!ag-bg-white !ag-border-neutral-200 ag-left-auto ag-right-0 ag-h-[150px]"
          className="ag-h-8 !ag-bg-white !ag-border-neutral-200 !ag-text-neutral-700"
          wrapperClassName="!ag-bg-white !ag-text-neutral-700 !ag-border-neutral-200 !ag-w-28"
          optionsClassName="ag-text-neutral-700 ag-border-neutral-200 ag-text-sm ag-py-1 hover:ag-bg-neutral-200/80 dark:hover:ag-bg-[#262626]/80"
        />
      </div>
      <Divider />
      <div className="ag-flex ag-items-center ag-justify-between">
        <span className="ag-text-neutral-700 ag-font-medium">Camera</span>
        <Switch
          onChange={(value) => formik.setFieldValue('widget.camera', value)}
          checked={formik.values.widget.camera}
        />
      </div>
      <div className="ag-flex ag-items-center ag-justify-between">
        <span className="ag-text-neutral-700 ag-font-medium">Screen share</span>
        <Switch
          onChange={(value) =>
            formik.setFieldValue('widget.screen_share', value)
          }
          checked={formik.values.widget.screen_share}
        />
      </div>
      <div className="ag-flex ag-items-center ag-justify-between ag-flex-wrap">
        <span className="ag-text-neutral-700 ag-font-medium">
          Language selection
        </span>
        <Switch
          onChange={(value) =>
            formik.setFieldValue('widget.language_selection', value)
          }
          checked={formik.values.widget.language_selection}
        />
      </div>
      <div>
        <div className="ag-flex ag-items-center ag-justify-between ag-flex-wrap">
          <span className="ag-text-neutral-700 ag-font-medium">
            Captions by default
          </span>
          <Switch
            onChange={(value) =>
              formik.setFieldValue('widget.captions_by_default', value)
            }
            checked={formik.values.widget.captions_by_default}
          />
        </div>
        <div className="ag-text-neutral-600 ag-text-sm">
          If disabled, users need to enable them from the menu
        </div>
      </div>
      <>
        <Divider />
        <div className="ag-flex ag-items-center ag-justify-between">
          <div className="ag-text-neutral-700 ag-font-medium">
            <div>Bubble Icon Side</div>
            <div className="ag-text-neutral-600 ag-text-sm">
              {formik.values.widget.bubble_icon_side === 'right'
                ? 'Right side'
                : 'Left side'}
            </div>
          </div>
          <Switch
            onChange={(value) =>
              formik.setFieldValue(
                'widget.bubble_icon_side',
                value ? 'right' : 'left'
              )
            }
            checked={formik.values.widget.bubble_icon_side === 'right'}
          />
        </div>
        <Divider />
        <Field
          label="Custom Widget Trigger(Selector)"
          name="customWidgetTrigger"
          className="ag-text-base"
          formik={formik}
        />
        <Divider />
        <div>
          <div className="ag-text-neutral-700 ag-text-base ag-font-medium ag-mb-2">
            Minimised chat (bubble)
          </div>
          <div className="ag-flex ag-items-center ag-gap-2 ag-cursor-pointer">
            {formik.values.widget.bubble_icon_url ? (
              <div
                onClick={() => {
                  formik.setFieldValue('widget.bubble_icon_custom', true);
                  formik.setFieldValue('widget.bubble_icon_variant', 'custom');
                }}
                className={cn(
                  'ag-py-4 ag-px-6 !ag-w-[92.88px] !ag-h-custom-72 ag-flex ag-flex-col ag-justify-center ag-items-center ag-border ag-rounded-lg ag-relative',
                  formik.values.widget.bubble_icon_custom
                    ? 'ag-border-primary-600'
                    : 'ag-border-neutral-200'
                )}
              >
                <div
                  onClick={() => {
                    formik.setFieldValue('widget.bubble_icon_custom', false);
                    formik.setFieldValue('widget.bubble_icon_url', '');
                  }}
                  className="ag-w-4 ag-h-4 ag-flex ag-items-center ag-justify-center ag-border ag-cursor-pointer ag-border-neutral-600 ag-absolute -ag-right-1 -ag-top-1 ag-text-neutral-600 ag-bg-white ag-rounded-full"
                >
                  <BiX size={12} />
                </div>
                {isLoading ? (
                  <Loader size={24} />
                ) : (
                  <img
                    className="ag-w-10 ag-h-10 ag-object-cover"
                    src={formik.values.widget.bubble_icon_url}
                  />
                )}
              </div>
            ) : (
              <div className="">
                <UploadPlusArea
                  type="file"
                  multiple={true}
                  name={'upload-bubble-icon'}
                  accept="image/png, image/jpeg, image/jpg"
                  id={'upload-bubble-icon'}
                  className="ag-py-4 !ag-w-[92.88px] !ag-h-custom-72 ag-px-8 ag-border ag-rounded-lg"
                  onChange={handleUpload}
                />
              </div>
            )}

            {Object.values(bubbleIcons).map((icon) => (
              <div
                onClick={() => {
                  formik.setFieldValue('widget.bubble_icon_custom', false);
                  formik.setFieldValue(
                    'widget.bubble_icon_variant',
                    icon.variant
                  );
                }}
                key={icon.variant}
                className={cn(
                  'ag-py-4 ag-px-8 !ag-w-[92.88px] !ag-h-custom-72 ag-flex ag-flex-col ag-justify-center ag-items-center ag-border ag-rounded-lg',
                  formik.values.widget.bubble_icon_variant === icon.variant &&
                    !formik.values.widget.bubble_icon_url
                    ? 'ag-border-primary-600'
                    : 'ag-border-neutral-200'
                )}
              >
                {icon.draw(
                  formik.values.widget.bubble_background_color,
                  formik.values.widget.bubble_icon_color
                )}
              </div>
            ))}
          </div>
        </div>
      </>
    </>
  );
};
